import type { ComponentType, ReactElement, ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
  CenteredSpinner,
  getHostMode,
  LazyBoundary,
  lazyPage,
  legacyPathTarget,
  NotFound,
} from '@l2u/web-shared';
import { useAdminAuth } from './lib/auth/admin.tsx';
import { AdminAcceptInvite } from './auth/AdminAcceptInvite.tsx';
import { AdminLogin } from './auth/AdminLogin.tsx';
import { ForgotPassword } from './auth/ForgotPassword.tsx';
import { ResetPassword } from './auth/ResetPassword.tsx';
import { AdminShell } from './admin/AdminShell.tsx';

// Route-level code splitting: the four sign-in screens and the shell stay in
// the entry chunk (a cold visit paints one of them first); every page is its
// own chunk, fetched on first navigation. Modules several pages share (the
// review components, detail-page sections) land in shared chunks the bundler
// derives. lazyPage retries a failed import and reloads once on a stale
// deploy; see packages/web-shared/src/lib/lazyPage.tsx.
const AdminDashboard = lazyPage(() =>
  import('./admin/Dashboard.tsx').then((m) => m.AdminDashboard),
);
const AdminQueue = lazyPage(() => import('./admin/Queue.tsx').then((m) => m.AdminQueue));
const AdminCompanies = lazyPage(() =>
  import('./admin/Companies.tsx').then((m) => m.AdminCompanies),
);
const AdminCompanyDetail = lazyPage(() =>
  import('./admin/CompanyDetail.tsx').then((m) => m.AdminCompanyDetail),
);
const AdminQuestionBuilder = lazyPage(() =>
  import('./admin/QuestionBuilder.tsx').then((m) => m.AdminQuestionBuilder),
);
const AdminQuestionnaires = lazyPage(() =>
  import('./admin/Questionnaires.tsx').then((m) => m.AdminQuestionnaires),
);
const AdminDocuments = lazyPage(() =>
  import('./admin/Documents.tsx').then((m) => m.AdminDocuments),
);
const AdminLibrary = lazyPage(() => import('./admin/Library.tsx').then((m) => m.AdminLibrary));
const AdminDocumentTypes = lazyPage(() =>
  import('./admin/DocumentTypes.tsx').then((m) => m.AdminDocumentTypes),
);
const AdminVerifications = lazyPage(() =>
  import('./admin/Verifications.tsx').then((m) => m.AdminVerifications),
);
const AdminTraining = lazyPage(() => import('./admin/Training.tsx').then((m) => m.AdminTraining));
const AdminAssets = lazyPage(() => import('./admin/Assets.tsx').then((m) => m.AdminAssets));
const AdminDrivers = lazyPage(() => import('./admin/Drivers.tsx').then((m) => m.AdminDrivers));
const AdminDriverDetail = lazyPage(() =>
  import('./admin/DriverDetail.tsx').then((m) => m.AdminDriverDetail),
);
const AdminAssetDetail = lazyPage(() =>
  import('./admin/AssetDetail.tsx').then((m) => m.AdminAssetDetail),
);
const AdminAuditRunner = lazyPage(() =>
  import('./admin/AuditRunner.tsx').then((m) => m.AdminAuditRunner),
);
const AdminFileNotePage = lazyPage(() =>
  import('./admin/FileNotePage.tsx').then((m) => m.AdminFileNotePage),
);
const More = lazyPage(() => import('./admin/More.tsx').then((m) => m.More));
const MyAudits = lazyPage(() => import('./admin/MyAudits.tsx').then((m) => m.MyAudits));
const AdminCorrectiveActions = lazyPage(() =>
  import('./admin/CorrectiveActions.tsx').then((m) => m.AdminCorrectiveActions),
);
const AdminExpiringDocuments = lazyPage(() =>
  import('./admin/ExpiringDocuments.tsx').then((m) => m.AdminExpiringDocuments),
);
const AdminBulkEmails = lazyPage(() =>
  import('./admin/BulkEmails.tsx').then((m) => m.AdminBulkEmails),
);
const AdminBulkEmailCompose = lazyPage(() =>
  import('./admin/BulkEmailCompose.tsx').then((m) => m.AdminBulkEmailCompose),
);
const AdminBulkEmailDetail = lazyPage(() =>
  import('./admin/BulkEmailDetail.tsx').then((m) => m.AdminBulkEmailDetail),
);
const AdminSettings = lazyPage(() => import('./admin/Settings.tsx').then((m) => m.AdminSettings));
const AdminVisitors = lazyPage(() => import('./admin/Visitors.tsx').then((m) => m.AdminVisitors));
const AdminReports = lazyPage(() => import('./admin/Reports.tsx').then((m) => m.AdminReports));
const AdminActivity = lazyPage(() => import('./admin/Activity.tsx').then((m) => m.AdminActivity));

/**
 * A lazy page in its own spinner/error boundary, inside the shell's outlet so
 * the sidebar stays usable while a chunk loads. Keyed by route so a failure on
 * one page does not stick to the next one rendered in the same outlet slot.
 */
function lazyRoute(key: string, Page: ComponentType): ReactElement {
  return (
    <LazyBoundary key={key}>
      <Page />
    </LazyBoundary>
  );
}

/**
 * Keeps reviewers out of the admin-only pages when they type the URL directly —
 * the sidebar already omits the rows (adminNav's `navForRole`), and the API
 * refuses the same surface with `requireAdminRole`. A silent redirect to the
 * dashboard rather than a 403 screen, matching the shell's wrong-persona idiom.
 */
function RoleGate({ children }: { children: ReactNode }) {
  const { user, loading } = useAdminAuth();
  if (loading) return <CenteredSpinner />;
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Forwards a surviving `/admin`-prefixed URL to its short form, query and hash
// intact — the reset/invite token lives in one of those.
function LegacyAdminRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={legacyPathTarget('/admin', location.pathname, location.search, location.hash)}
      replace
    />
  );
}

export function App() {
  const hostMode = getHostMode();
  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AdminAcceptInvite />} />

      {/* Admin */}
      <Route path="/" element={<AdminShell />}>
        <Route index element={lazyRoute('AdminDashboard', AdminDashboard)} />
        <Route path="queue" element={lazyRoute('AdminQueue', AdminQueue)} />
        <Route path="companies" element={lazyRoute('AdminCompanies', AdminCompanies)} />
        <Route path="companies/:id" element={lazyRoute('AdminCompanyDetail', AdminCompanyDetail)} />
        <Route path="drivers" element={lazyRoute('AdminDrivers', AdminDrivers)} />
        <Route path="drivers/:id" element={lazyRoute('AdminDriverDetail', AdminDriverDetail)} />
        <Route path="assets" element={lazyRoute('AdminAssets', AdminAssets)} />
        <Route path="assets/:id" element={lazyRoute('AdminAssetDetail', AdminAssetDetail)} />
        {/* Every staff member's own assigned audits — an audit is assigned to
            the admin who created it. Added for the mobile Audits tab; the
            desktop sidebar still has no entry for it. */}
        <Route path="audits" element={lazyRoute('MyAudits', MyAudits)} />
        <Route path="audits/:id" element={lazyRoute('AdminAuditRunner', AdminAuditRunner)} />
        {/* A file note (draft or published). The publish / reply emails link
            here; their legacy `/admin/file-notes/:id` form is forwarded by
            LegacyAdminRedirect below. */}
        <Route path="file-notes/:id" element={lazyRoute('AdminFileNotePage', AdminFileNotePage)} />
        <Route
          path="corrective-actions"
          element={lazyRoute('AdminCorrectiveActions', AdminCorrectiveActions)}
        />
        <Route path="visitors" element={lazyRoute('AdminVisitors', AdminVisitors)} />
        {/* Admin-only surface — mirrored by requireAdminRole on the API and by
            adminNav's ADMIN_ONLY_PATH_PREFIXES. Keep the three in step. */}
        <Route
          path="reports"
          element={
            <RoleGate>
              {lazyRoute('AdminReports', AdminReports)}
            </RoleGate>
          }
        />
        <Route
          path="questionnaires"
          element={
            <RoleGate>
              {lazyRoute('AdminQuestionnaires', AdminQuestionnaires)}
            </RoleGate>
          }
        />
        <Route
          path="questions"
          element={
            <RoleGate>
              {lazyRoute('AdminQuestionBuilder', AdminQuestionBuilder)}
            </RoleGate>
          }
        />
        <Route path="documents" element={lazyRoute('AdminDocuments', AdminDocuments)} />
        <Route
          path="document-types"
          element={lazyRoute('AdminDocumentTypes', AdminDocumentTypes)}
        />
        <Route
          path="training"
          element={
            <RoleGate>
              {lazyRoute('AdminTraining', AdminTraining)}
            </RoleGate>
          }
        />
        <Route path="library" element={lazyRoute('AdminLibrary', AdminLibrary)} />
        <Route path="verifications" element={lazyRoute('AdminVerifications', AdminVerifications)} />
        <Route
          path="expiring-documents"
          element={lazyRoute('AdminExpiringDocuments', AdminExpiringDocuments)}
        />
        {/* Admin-only: requireAdminRole on /api/bulk-emails, ADMIN_ONLY_PATH_PREFIXES. */}
        <Route
          path="bulk-email"
          element={
            <RoleGate>
              {lazyRoute('AdminBulkEmails', AdminBulkEmails)}
            </RoleGate>
          }
        />
        <Route
          path="bulk-email/new"
          element={
            <RoleGate>
              {lazyRoute('AdminBulkEmailCompose', AdminBulkEmailCompose)}
            </RoleGate>
          }
        />
        <Route
          path="bulk-email/:id"
          element={
            <RoleGate>
              {lazyRoute('AdminBulkEmailDetail', AdminBulkEmailDetail)}
            </RoleGate>
          }
        />
        <Route path="users" element={<Navigate to="/settings?tab=users" replace />} />
        <Route
          path="settings"
          element={
            <RoleGate>
              {lazyRoute('AdminSettings', AdminSettings)}
            </RoleGate>
          }
        />
        {/* Not wrapped in RoleGate: /api/activity is region-scoped, so a
            reviewer sees their own regions' history. See routes/activity.ts. */}
        <Route path="activity" element={lazyRoute('AdminActivity', AdminActivity)} />
        {/* Mobile-only overflow of the bottom tab bar (see More.tsx). */}
        <Route path="more" element={lazyRoute('More', More)} />
      </Route>

      {/* Legacy `/admin`-prefixed URLs, mounted ONLY in audiences mode. In
          legacy mode the router basename is already `/admin`, so the prefix is
          consumed before matching — and on a shared wildcard host that prefix
          is how Cloudflare picks the admin Worker over the portal one, so
          forwarding it away would send the browser to another Worker's path.
          Kept because invite and reset emails already sent are prefixed and
          cannot be recalled. Deletable with the Prod deletion PR. */}
      {hostMode === 'audiences' && (
        <Route path="/admin/*" element={<LegacyAdminRedirect />} />
      )}

      {/* '/' is resolved against the router basename, so this is the dashboard
          in both modes: /admin on the shared host, / on the admin host. */}
      <Route path="*" element={<NotFound links={[{ to: '/', label: 'Dashboard' }]} />} />
    </Routes>
  );
}
